/**
 * Inline SVG illustrations for the onboarding tour. All use the same lime
 * + ink palette as the rest of inonit, scale crisply at any DPI, and add
 * zero network cost (no external images).
 */

// Soft hand-drawn dot pattern shared by a few scenes.
function DotsPattern({ id }) {
  return (
    <pattern id={id} x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
      <circle cx="2" cy="2" r="1.2" fill="rgba(10,10,10,0.18)" />
    </pattern>
  );
}

export function WelcomeIllustration() {
  return (
    <svg viewBox="0 0 320 200" width="100%" height="100%" aria-hidden="true">
      <defs><DotsPattern id="dots-welcome" /></defs>
      <rect x="0" y="0" width="320" height="200" fill="url(#dots-welcome)" />
      {/* Big waving hand */}
      <g transform="translate(80 30)">
        <rect x="0" y="20" width="160" height="120" rx="32" fill="#F4F0E1" stroke="#0A0A0A" strokeWidth="6" />
        <text x="80" y="100" textAnchor="middle" fontSize="64" fontFamily="Outfit, system-ui" fontWeight="800" fill="#0A0A0A">👋</text>
      </g>
      {/* "Hi!" pill */}
      <g transform="translate(220 110)">
        <rect x="0" y="0" width="80" height="36" rx="18" fill="#0A0A0A" />
        <text x="40" y="24" textAnchor="middle" fontSize="14" fontFamily="Outfit, system-ui" fontWeight="800" fill="#D6FF00" letterSpacing="2">HI!</text>
      </g>
    </svg>
  );
}

export function DiscoverIllustration() {
  return (
    <svg viewBox="0 0 320 200" width="100%" height="100%" aria-hidden="true">
      <defs><DotsPattern id="dots-disc" /></defs>
      <rect x="0" y="0" width="320" height="200" fill="url(#dots-disc)" />
      {/* Map base */}
      <rect x="24" y="24" width="220" height="152" rx="22" fill="#F4F0E1" stroke="#0A0A0A" strokeWidth="5" />
      {/* Roads */}
      <path d="M30 80 Q120 60 240 90" stroke="rgba(10,10,10,0.22)" strokeWidth="4" fill="none" />
      <path d="M30 130 Q150 110 240 145" stroke="rgba(10,10,10,0.22)" strokeWidth="4" fill="none" />
      <path d="M120 30 Q140 100 130 170" stroke="rgba(10,10,10,0.22)" strokeWidth="4" fill="none" />
      {/* Pins */}
      {[
        { x: 90, y: 90 },
        { x: 170, y: 70 },
        { x: 200, y: 140 },
      ].map((p, i) => (
        <g key={i} transform={`translate(${p.x} ${p.y})`}>
          <path d="M0 0 c8 -10 22 -10 22 0 c0 8 -11 18 -11 26 c0 -8 -11 -18 -11 -26 z"
                fill={i === 1 ? "#0A0A0A" : "#F4F0E1"}
                stroke="#0A0A0A" strokeWidth="3" transform="translate(-11 -20)" />
          <circle cx="0" cy="-12" r="3" fill={i === 1 ? "#D6FF00" : "#0A0A0A"} />
        </g>
      ))}
      {/* Radius pill */}
      <g transform="translate(254 28)">
        <rect x="0" y="0" width="48" height="26" rx="13" fill="#0A0A0A" />
        <text x="24" y="18" textAnchor="middle" fontSize="11" fontFamily="Outfit, system-ui" fontWeight="800" fill="#D6FF00" letterSpacing="1.4">25 KM</text>
      </g>
      {/* Compass */}
      <g transform="translate(254 168)">
        <circle r="14" fill="#F4F0E1" stroke="#0A0A0A" strokeWidth="3" />
        <path d="M0 -8 L4 0 L0 8 L-4 0 Z" fill="#0A0A0A" />
      </g>
    </svg>
  );
}

export function JoinIllustration() {
  return (
    <svg viewBox="0 0 320 200" width="100%" height="100%" aria-hidden="true">
      <defs><DotsPattern id="dots-join" /></defs>
      <rect x="0" y="0" width="320" height="200" fill="url(#dots-join)" />
      {/* Event card */}
      <g transform="translate(40 28)">
        <rect width="240" height="144" rx="22" fill="#F4F0E1" stroke="#0A0A0A" strokeWidth="5" />
        <rect x="14" y="14" width="100" height="56" rx="12" fill="#0A0A0A" />
        <text x="64" y="50" textAnchor="middle" fontSize="11" fontFamily="Outfit, system-ui" fontWeight="800" fill="#D6FF00" letterSpacing="1.4">SUNDAY</text>
        <rect x="14" y="78" width="160" height="10" rx="5" fill="rgba(10,10,10,0.85)" />
        <rect x="14" y="94" width="120" height="8" rx="4" fill="rgba(10,10,10,0.35)" />
        {/* Join button */}
        <g transform="translate(124 14)">
          <rect width="100" height="34" rx="17" fill="#D6FF00" stroke="#0A0A0A" strokeWidth="3.5" />
          <text x="50" y="22" textAnchor="middle" fontSize="11" fontFamily="Outfit, system-ui" fontWeight="800" fill="#0A0A0A" letterSpacing="1.6">JOIN</text>
        </g>
        {/* Attendee avatars */}
        <g transform="translate(14 112)">
          {[0, 1, 2, 3].map((i) => (
            <circle key={i} cx={i * 18 + 14} cy="14" r="13" fill={i === 0 ? "#D6FF00" : "#F4F0E1"} stroke="#0A0A0A" strokeWidth="3" />
          ))}
          <text x="100" y="19" fontSize="11" fontFamily="Outfit, system-ui" fontWeight="700" fill="rgba(10,10,10,0.6)">+12 going</text>
        </g>
      </g>
      {/* Cursor */}
      <path d="M236 142 l14 6 l-6 4 l4 8 l-6 3 l-4 -8 l-6 4 z" fill="#0A0A0A" />
    </svg>
  );
}

export function HostIllustration() {
  return (
    <svg viewBox="0 0 320 200" width="100%" height="100%" aria-hidden="true">
      <defs><DotsPattern id="dots-host" /></defs>
      <rect x="0" y="0" width="320" height="200" fill="url(#dots-host)" />
      {/* "Create" sheet */}
      <g transform="translate(40 22)">
        <rect width="240" height="156" rx="22" fill="#F4F0E1" stroke="#0A0A0A" strokeWidth="5" />
        <text x="20" y="34" fontSize="11" fontFamily="Outfit, system-ui" fontWeight="800" fill="rgba(10,10,10,0.55)" letterSpacing="2.4">NEW EVENT</text>
        {/* Title input */}
        <rect x="20" y="46" width="200" height="22" rx="11" fill="#FFFFFF" stroke="#0A0A0A" strokeWidth="3" />
        <text x="32" y="62" fontSize="12" fontFamily="Outfit, system-ui" fontWeight="700" fill="#0A0A0A">Sunday box cricket</text>
        {/* Category chips */}
        {["Sports","Food","Music"].map((c, i) => (
          <g key={c} transform={`translate(${20 + i * 60} 78)`}>
            <rect width="54" height="22" rx="11" fill={i === 0 ? "#D6FF00" : "#F4F0E1"} stroke="#0A0A0A" strokeWidth="3" />
            <text x="27" y="15" textAnchor="middle" fontSize="9" fontFamily="Outfit, system-ui" fontWeight="800" fill="#0A0A0A" letterSpacing="1.2">{c.toUpperCase()}</text>
          </g>
        ))}
        {/* Big publish CTA */}
        <g transform="translate(20 116)">
          <rect width="200" height="28" rx="14" fill="#0A0A0A" />
          <text x="100" y="19" textAnchor="middle" fontSize="11" fontFamily="Outfit, system-ui" fontWeight="800" fill="#D6FF00" letterSpacing="2">PUBLISH</text>
        </g>
      </g>
      {/* Floating + */}
      <g transform="translate(268 138)">
        <circle r="20" fill="#D6FF00" stroke="#0A0A0A" strokeWidth="4" />
        <path d="M-9 0 L9 0 M0 -9 L0 9" stroke="#0A0A0A" strokeWidth="4" strokeLinecap="round" />
      </g>
    </svg>
  );
}

export function ConnectIllustration() {
  return (
    <svg viewBox="0 0 320 200" width="100%" height="100%" aria-hidden="true">
      <defs><DotsPattern id="dots-conn" /></defs>
      <rect x="0" y="0" width="320" height="200" fill="url(#dots-conn)" />
      {/* Three faces approaching each other */}
      {[
        { x: 80, y: 100, c: "#F4F0E1" },
        { x: 160, y: 80, c: "#D6FF00" },
        { x: 240, y: 110, c: "#F4F0E1" },
      ].map((p, i) => (
        <g key={i} transform={`translate(${p.x} ${p.y})`}>
          <circle r="36" fill={p.c} stroke="#0A0A0A" strokeWidth="5" />
          <text y="6" textAnchor="middle" fontSize="11" fontFamily="Outfit, system-ui" fontWeight="800" fill="#0A0A0A" letterSpacing="2">
            {["M", "A", "K"][i]}
          </text>
        </g>
      ))}
      {/* Connecting lines */}
      <path d="M105 92 Q130 70 145 80" stroke="#0A0A0A" strokeWidth="3" fill="none" strokeDasharray="4 4" />
      <path d="M180 84 Q200 92 220 100" stroke="#0A0A0A" strokeWidth="3" fill="none" strokeDasharray="4 4" />
      {/* "Nearby" badge */}
      <g transform="translate(112 30)">
        <rect width="98" height="28" rx="14" fill="#0A0A0A" />
        <text x="49" y="19" textAnchor="middle" fontSize="11" fontFamily="Outfit, system-ui" fontWeight="800" fill="#D6FF00" letterSpacing="1.6">NEARBY · BETA</text>
      </g>
    </svg>
  );
}

export function ReadyIllustration() {
  return (
    <svg viewBox="0 0 320 200" width="100%" height="100%" aria-hidden="true">
      <defs><DotsPattern id="dots-ready" /></defs>
      <rect x="0" y="0" width="320" height="200" fill="url(#dots-ready)" />
      {/* Confetti */}
      {[
        [40, 30, "#0A0A0A"], [80, 60, "#0A0A0A"], [120, 30, "#F4F0E1"],
        [200, 30, "#0A0A0A"], [240, 60, "#F4F0E1"], [280, 30, "#0A0A0A"],
        [60, 160, "#0A0A0A"], [260, 160, "#F4F0E1"], [100, 170, "#F4F0E1"],
      ].map(([x, y, c], i) => (
        <rect key={i} x={x} y={y} width="10" height="14" rx="2" fill={c} stroke="#0A0A0A" strokeWidth="2" transform={`rotate(${i * 35} ${x + 5} ${y + 7})`} />
      ))}
      {/* Spotlight circle with checkmark */}
      <g transform="translate(160 100)">
        <circle r="56" fill="#0A0A0A" />
        <circle r="48" fill="#D6FF00" />
        <path d="M-20 0 L-6 14 L22 -14" fill="none" stroke="#0A0A0A" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}
